import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Select, Tag, Row, Col,
  Drawer, Form, message, InputNumber, Input, Modal, Tabs, Statistic,
} from 'antd'
import { SwapOutlined, PlusOutlined, ArrowRightOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography
const API = ''

const STAGES = ['GODOWN', 'BLOWROOM', 'CARDING', 'DRAWING', 'SIMPLEX', 'RING_FRAME', 'AUTO_CONER', 'FG_STORE']

const MATERIAL_TYPES = ['COTTON_BALES', 'SLIVER', 'ROVING', 'YARN_COPS', 'YARN_CONES', 'WASTE']

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default',
  ISSUED: 'processing',
  RECEIVED: 'success',
  CANCELLED: 'error',
}

const STAGE_COLORS: Record<string, string> = {
  GODOWN: 'blue',
  BLOWROOM: 'orange',
  CARDING: 'purple',
  DRAWING: 'cyan',
  SIMPLEX: 'magenta',
  RING_FRAME: 'gold',
  AUTO_CONER: 'lime',
  FG_STORE: 'green',
}

export default function IDTPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()
  const [statusTab, setStatusTab] = useState('ALL')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [receiveModal, setReceiveModal] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [receiveWeight, setReceiveWeight] = useState<number | null>(null)
  const [form] = Form.useForm()

  const statusParam = statusTab === 'ALL' ? undefined : statusTab

  const { data: idts, isLoading } = useQuery({
    queryKey: ['idt', plantId, statusTab],
    queryFn: () =>
      apiClient.get(`${API}/idt`, {
        params: { plant_id: plantId, status: statusParam, limit: 200 },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: sectionStock } = useQuery({
    queryKey: ['section-stock', plantId],
    queryFn: () =>
      apiClient.get(`${API}/section-stock`, { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: mixingPlans } = useQuery({
    queryKey: ['mixing-plans-approved', plantId],
    queryFn: () =>
      apiClient.get(`${API}/mixing-plans`, {
        params: { plant_id: plantId, status: 'APPROVED', limit: 200 },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.post(`${API}/idt`, { ...data, plant_id: plantId }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['idt'] })
      qc.invalidateQueries({ queryKey: ['section-stock'] })
      message.success('IDT created')
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const issueMut = useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`${API}/idt/${id}/issue`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['idt'] })
      qc.invalidateQueries({ queryKey: ['section-stock'] })
      message.success('IDT issued')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const receiveMut = useMutation({
    mutationFn: ({ id, received_weight_kg }: { id: string; received_weight_kg: number }) =>
      apiClient.post(`${API}/idt/${id}/receive`, { received_weight_kg }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['idt'] })
      qc.invalidateQueries({ queryKey: ['section-stock'] })
      message.success('IDT received')
      setReceiveModal(null)
      setReceiveWeight(null)
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const idtList = Array.isArray(idts) ? idts : []
  const mixingList = Array.isArray(mixingPlans) ? mixingPlans : []

  // Aggregate section stock by stage
  const stockList = Array.isArray(sectionStock) ? sectionStock : []
  const stockByStage: Record<string, { total_kg: number; lot_count: number }> = {}
  stockList.forEach((s: any) => {
    if (!stockByStage[s.stage]) stockByStage[s.stage] = { total_kg: 0, lot_count: 0 }
    stockByStage[s.stage].total_kg += Number(s.balance_kg ?? 0)
    stockByStage[s.stage].lot_count += 1
  })

  const expandedRowRender = (record: any) => {
    const lines = Array.isArray(record.lines) ? record.lines : []
    return (
      <Table
        size="small"
        dataSource={lines}
        rowKey="id"
        pagination={false}
        columns={[
          { title: 'Lot No', dataIndex: 'lot_no', key: 'lot_no' },
          { title: 'Grade', dataIndex: 'grade', key: 'grade', width: 70 },
          {
            title: 'Weight (kg)', dataIndex: 'weight_kg', key: 'weight_kg', align: 'right' as const,
            render: (v: number) => Number(v).toFixed(3),
          },
          { title: 'Bale ID', dataIndex: 'bale_id', key: 'bale_id', render: (v: string) => v || '—' },
        ]}
      />
    )
  }

  const columns = [
    {
      title: 'IDT No', dataIndex: 'idt_no', key: 'idt_no', width: 130,
      render: (v: string) => <b style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</b>,
    },
    {
      title: 'Date', dataIndex: 'created_at', key: 'date', width: 110,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YY') : '—',
    },
    {
      title: 'From → To', key: 'flow', width: 200,
      render: (_: unknown, r: any) => (
        <Space size={4}>
          <Tag color={STAGE_COLORS[r.from_stage] ?? 'default'}>{r.from_stage?.replace('_', ' ')}</Tag>
          <ArrowRightOutlined style={{ fontSize: 10 }} />
          <Tag color={STAGE_COLORS[r.to_stage] ?? 'default'}>{r.to_stage?.replace('_', ' ')}</Tag>
        </Space>
      ),
    },
    {
      title: 'Material', dataIndex: 'material_type', key: 'material_type', width: 120,
      render: (v: string) => <Tag>{v?.replace('_', ' ')}</Tag>,
    },
    { title: 'Lot No', dataIndex: 'lot_no', key: 'lot_no', width: 110 },
    {
      title: 'Total Wt (kg)', dataIndex: 'total_weight_kg', key: 'total_weight_kg', width: 120, align: 'right' as const,
      render: (v: number) => v != null ? Number(v).toFixed(3) : '—',
    },
    {
      title: 'Rcvd Wt (kg)', dataIndex: 'received_weight_kg', key: 'received_weight_kg', width: 120, align: 'right' as const,
      render: (v: number) => v != null ? Number(v).toFixed(3) : '—',
    },
    {
      title: 'Variance', key: 'variance', width: 100, align: 'right' as const,
      render: (_: unknown, r: any) => {
        if (r.received_weight_kg == null || r.total_weight_kg == null) return '—'
        const diff = Number(r.received_weight_kg) - Number(r.total_weight_kg)
        const color = diff < 0 ? 'red' : '#52c41a'
        return <span style={{ color, fontWeight: 600 }}>{diff.toFixed(3)}</span>
      },
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 150,
      render: (_: unknown, r: any) => (
        <Space size={4}>
          {r.status === 'DRAFT' && (
            <Button
              size="small"
              type="primary"
              onClick={() => issueMut.mutate(r.id)}
              loading={issueMut.isPending}
            >
              Issue
            </Button>
          )}
          {r.status === 'ISSUED' && (
            <Button
              size="small"
              style={{ background: '#52c41a', color: '#fff', borderColor: '#52c41a' }}
              onClick={() => { setReceiveModal(r); setReceiveWeight(r.total_weight_kg) }}
            >
              Receive
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <SwapOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Inter-Department Transfer (IDT)
      </Title>
      <Typography.Text type="secondary">Track material flow between production stages</Typography.Text>

      {/* Section Stock Summary */}
      <Row gutter={12} style={{ marginTop: 16 }}>
        {STAGES.map(stage => {
          const info = stockByStage[stage] ?? { total_kg: 0, lot_count: 0 }
          return (
            <Col key={stage} flex="1">
              <Card size="small" style={{ borderTop: `3px solid var(--ant-color-primary)` }}>
                <Statistic
                  title={<span style={{ fontSize: 11 }}>{stage.replace('_', ' ')}</span>}
                  value={info.total_kg.toFixed(0)}
                  suffix="kg"
                  valueStyle={{ fontSize: 16 }}
                />
                <div style={{ fontSize: 11, color: '#888' }}>{info.lot_count} lots</div>
              </Card>
            </Col>
          )
        })}
      </Row>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
          <Col>
            <Select
              value={plantId}
              onChange={setPlantId}
              style={{ width: 180 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
            />
          </Col>
          <Col flex="auto">
            <Tabs
              activeKey={statusTab}
              onChange={setStatusTab}
              size="small"
              items={['ALL', 'DRAFT', 'ISSUED', 'RECEIVED'].map(s => ({ key: s, label: s }))}
              style={{ marginBottom: 0 }}
            />
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setDrawerOpen(true) }}>
              New IDT
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={idtList}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          expandable={{ expandedRowRender }}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          scroll={{ x: 1300 }}
        />
      </Card>

      {/* Create IDT Drawer */}
      <Drawer
        title="New IDT"
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); form.resetFields() }}
        width={560}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setDrawerOpen(false); form.resetFields() }}>Cancel</Button>
              <Button
                type="primary"
                loading={createMut.isPending}
                onClick={() =>
                  form.validateFields().then(vals => {
                    // compute total from lines
                    const lines = vals.lines ?? []
                    const total = lines.reduce((s: number, l: any) => s + Number(l.weight_kg ?? 0), 0)
                    createMut.mutate({ ...vals, total_weight_kg: total })
                  })
                }
              >
                Save
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="from_stage" label="From Stage" rules={[{ required: true }]}>
                <Select options={STAGES.map(s => ({ value: s, label: s.replace('_', ' ') }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="to_stage" label="To Stage" rules={[{ required: true }]}>
                <Select options={STAGES.map(s => ({ value: s, label: s.replace('_', ' ') }))} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="material_type" label="Material Type" rules={[{ required: true }]}>
                <Select options={MATERIAL_TYPES.map(t => ({ value: t, label: t.replace('_', ' ') }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lot_no" label="Lot No">
                <Input placeholder="e.g. LOT-2024-001" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="mixing_plan_id" label="Mixing Plan (optional)">
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              options={mixingList.map((m: any) => ({
                value: m.id,
                label: `${m.mx_no} — ${m.count_description ?? ''}`,
              }))}
            />
          </Form.Item>

          <Form.Item name="remarks" label="Remarks">
            <Input />
          </Form.Item>

          {/* IDT Lines */}
          <Typography.Text strong>Lines</Typography.Text>
          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Row gutter={8} key={key} style={{ marginTop: 8 }} align="middle">
                    <Col span={7}>
                      <Form.Item name={[name, 'lot_no']} style={{ marginBottom: 0 }}>
                        <Input placeholder="Lot No" size="small" />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item name={[name, 'grade']} style={{ marginBottom: 0 }}>
                        <Input placeholder="Grade" size="small" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name={[name, 'weight_kg']} style={{ marginBottom: 0 }}>
                        <InputNumber placeholder="Weight (kg)" size="small" style={{ width: '100%' }} min={0} precision={3} />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Button size="small" danger onClick={() => remove(name)}>Del</Button>
                    </Col>
                  </Row>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  size="small"
                  block
                  style={{ marginTop: 8 }}
                  icon={<PlusOutlined />}
                >
                  Add Line
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Drawer>

      {/* Receive Modal */}
      <Modal
        title={`Receive IDT: ${receiveModal?.idt_no}`}
        open={!!receiveModal}
        onCancel={() => { setReceiveModal(null); setReceiveWeight(null) }}
        onOk={() => {
          if (!receiveWeight) { message.warning('Enter received weight'); return }
          receiveMut.mutate({ id: receiveModal!.id, received_weight_kg: receiveWeight })
        }}
        confirmLoading={receiveMut.isPending}
      >
        <Form layout="vertical">
          <Form.Item label="Received Weight (kg)" required>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={3}
              value={receiveWeight}
              onChange={v => setReceiveWeight(v)}
            />
          </Form.Item>
          <div style={{ color: '#888', fontSize: 12 }}>
            Issued weight: {Number(receiveModal?.total_weight_kg ?? 0).toFixed(3)} kg
          </div>
        </Form>
      </Modal>
    </div>
  )
}
